import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Loader2, Building2, Users, Star, TrendingUp, Award, ThumbsUp, ThumbsDown,
    Zap, Crown, Gem, Activity, DollarSign, RefreshCw, AlertTriangle, Leaf,
    MousePointerClick, Eye, BarChart3, Trophy, Calendar, ChevronDown, Globe
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { API } from '../../config/apiBase';


// ==================== PLATAFORMA TAB (existing) ====================

const PLAN_META = {
    free: { label: 'Free', color: '#6B7280', icon: Star },
    lite: { label: 'Lite', color: '#10B981', icon: Leaf },
    start: { label: 'Start', color: '#F59E0B', icon: Zap },
    gestão: { label: 'Gestão', color: '#3B82F6', icon: Crown },
    pro: { label: 'Pro', color: '#8B5CF6', icon: Gem },
    enterprise: { label: 'Enterprise', color: 'var(--sv-gold-primary)', icon: Gem },
    sem_plano: { label: 'Sem plano', color: '#6B7280', icon: Building2 },
};

const MODULE_LABELS = {
    'grupos_ofertas': 'Grupos de Ofertas', 'indicações': 'Indicações', 'perfil': 'Perfil',
    'gestão_basica': 'Gestão Básica', 'copilot_básico': 'Copilot Básico', 'histórico_básico': 'Histórico Básico',
    'financeiro_básico': 'Financeiro Básico', 'financeiro_melhorado': 'Financeiro Melhorado',
    'financeiro_completo': 'Financeiro Completo', 'ponto_digital': 'Ponto Digital',
    'ponto_digital_inicial': 'Ponto Digital Inicial', 'ponto_digital_premium': 'Ponto Digital Premium',
    'solicitacoes': 'Solicitações', 'meu_histórico': 'Meu Histórico',
    'logo_personalizada': 'Logo Personalizada', 'exportacao_csv': 'Exportação CSV',
    'exportacao_csv_simples': 'CSV Simples', 'exportacao_pdf': 'Exportação PDF',
    'performance': 'Performance', 'performance_avancada': 'Performance Avançada',
    'permissões_avançadas': 'Permissões Avançadas', 'escalas_jornadas': 'Escalas e Jornadas',
    'escalas_jornadas_basicas': 'Escalas Básicas', 'auditoria_resumida': 'Auditoria Resumida',
    'auditoria_completa': 'Auditoria Completa', 'relatórios_avançados': 'Relatórios Avançados',
    'geofence_avancado': 'Geofence Avançado', 'radar_ofertas': 'Radar de Ofertas',
    'radar_ofertas_premium': 'Radar Premium', 'alertas_básico': 'Alertas Básico',
    'alertas_melhorado': 'Alertas Melhorado', 'contas_categorias': 'Contas e Categorias',
    'branding': 'Branding', 'financeiro_preview': 'Financeiro Preview',
};

function PlataformaTab({ data, events }) {
    if (!data) return null;
    const maxPlanCount = Math.max(1, ...Object.values(data.empresas_por_plano || {}));
    const maxModCount = Math.max(1, ...Object.values(data.modulos_ativos || {}));
    const npsColor = data.nps_score >= 50 ? '#22c55e' : data.nps_score >= 0 ? '#f59e0b' : '#ef4444';

    return (
        <div className="space-y-4">
            {events && (
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.08) 0%, rgba(16,185,129,0.04) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#22c55e' }} />
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Metricas em Tempo Real</h3>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-auto" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>Hoje: {events.today_events}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {(events.by_type || []).slice(0, 4).map(e => {
                        const colors = { deal_buy_click: '#22c55e', deal_detail_click: '#3B82F6', deal_view: '#8B5CF6', offer_request: '#F59E0B' };
                        const labels = { deal_buy_click: 'Compras', deal_detail_click: 'Detalhes', deal_view: 'Views', offer_request: 'Pedidos' };
                        const c = colors[e.event_type] || '#7DB3FF';
                        return (
                            <div key={e.event_type} className="rounded-lg p-2.5" style={{ background: `${c}08`, border: `1px solid ${c}15` }}>
                                <p className="text-lg font-bold" style={{ color: c, fontFamily: 'var(--font-display)' }}>{e.count}</p>
                                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{labels[e.event_type] || e.event_type}</p>
                            </div>
                        );
                    })}
                </div>
                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Total de eventos registrados: {events.total_events}</p>
            </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                    { label: 'MRR', value: `R$ ${(data.mrr || 0).toFixed(2)}`, icon: DollarSign, color: '#22c55e', sub: 'Receita recorrente mensal' },
                    { label: 'Conversao Trial', value: `${data.trial_conversion_rate || 0}%`, icon: TrendingUp, color: '#3B82F6', sub: `${data.total_paid || 0} pagantes de ${(data.total_paid || 0) + (data.churned || 0)} totais` },
                    { label: 'Churn Rate', value: `${data.churn_rate || 0}%`, icon: AlertTriangle, color: data.churn_rate > 10 ? '#ef4444' : '#f59e0b', sub: `${data.churned || 0} empresas churned` },
                    { label: 'Auto-renovacoes', value: data.auto_renewals || 0, icon: RefreshCw, color: '#8B5CF6', sub: 'Renovacoes automaticas' },
                ].map(k => (
                    <div key={k.label} className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${k.color}12` }}><k.icon size={14} style={{ color: k.color }} /></div></div>
                        <p className="text-xl font-bold" style={{ color: k.color, fontFamily: 'var(--font-display)' }}>{k.value}</p>
                        <p className="text-[10px] font-medium" style={{ color: 'var(--text)' }}>{k.label}</p>
                        <p className="text-[9px]" style={{ color: 'var(--muted)' }}>{k.sub}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {[
                    { label: 'Empresas', value: data.total_empresas, icon: Building2, color: 'var(--sv-gold-primary)' },
                    { label: 'Usuarios', value: data.total_users, icon: Users, color: '#3B82F6' },
                    { label: 'Colaboradores', value: data.total_colabs, icon: Users, color: '#8B5CF6' },
                    { label: 'Profissionais', value: data.total_profissionais, icon: Award, color: '#F59E0B' },
                    { label: 'Trials ativos', value: data.total_trials || 0, icon: Zap, color: '#22c55e' },
                ].map(k => (
                    <div key={k.label} className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${k.color}12` }}><k.icon size={14} style={{ color: k.color }} /></div></div>
                        <p className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>{k.value}</p>
                        <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{k.label}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Empresas por Plano</h3>
                    {Object.keys(data.empresas_por_plano || {}).length === 0 ? <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>Sem dados</p> : (
                        <div className="flex flex-col gap-2.5">
                            {Object.entries(data.empresas_por_plano).map(([plan, count]) => {
                                const meta = PLAN_META[plan] || PLAN_META.sem_plano;
                                return (
                                    <div key={plan} className="flex items-center gap-3">
                                        <div className="w-20 flex items-center gap-1.5"><meta.icon size={11} style={{ color: meta.color }} /><span className="text-xs font-medium capitalize" style={{ color: meta.color }}>{meta.label}</span></div>
                                        <div className="flex-1 h-6 rounded-lg overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                                            <div className="h-full rounded-lg flex items-center pl-2 transition-all" style={{ width: `${(count / maxPlanCount) * 100}%`, background: `${meta.color}25`, minWidth: 24 }}>
                                                <span className="text-[11px] font-bold" style={{ color: meta.color }}>{count}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>NPS das Indicacoes</h3>
                    <div className="flex items-center gap-6 mb-4">
                        <div className="text-center">
                            <p className="text-3xl font-bold" style={{ color: npsColor, fontFamily: 'var(--font-display)' }}>{data.nps_score}</p>
                            <p className="text-[10px]" style={{ color: 'var(--muted)' }}>NPS Score</p>
                        </div>
                        <div className="flex-1 flex flex-col gap-1.5">
                            <div className="flex items-center gap-2"><ThumbsUp size={12} style={{ color: '#22c55e' }} /><span className="text-[11px]" style={{ color: 'var(--muted)' }}>Promotores (4-5)</span><span className="text-xs font-bold ml-auto" style={{ color: '#22c55e' }}>{data.nps_data?.promoters || 0}</span></div>
                            <div className="flex items-center gap-2"><ThumbsDown size={12} style={{ color: '#ef4444' }} /><span className="text-[11px]" style={{ color: 'var(--muted)' }}>Detratores (1-2)</span><span className="text-xs font-bold ml-auto" style={{ color: '#ef4444' }}>{data.nps_data?.detractors || 0}</span></div>
                            <div className="flex items-center gap-2"><Activity size={12} style={{ color: 'var(--muted)' }} /><span className="text-[11px]" style={{ color: 'var(--muted)' }}>Total</span><span className="text-xs font-bold ml-auto" style={{ color: 'var(--text)' }}>{data.nps_data?.total || 0}</span></div>
                        </div>
                    </div>
                    <div className="flex items-end gap-1.5 h-16">
                        {[1, 2, 3, 4, 5].map(r => {
                            const count = data.rating_distribution?.[String(r)] || 0;
                            const maxR = Math.max(1, ...Object.values(data.rating_distribution || {}));
                            return (
                                <div key={r} className="flex-1 flex flex-col items-center gap-1">
                                    <div className="w-full rounded-t transition-all" style={{ height: `${(count / maxR) * 48}px`, background: r >= 4 ? '#22c55e' : r >= 3 ? '#f59e0b' : '#ef4444', minHeight: count > 0 ? 4 : 0 }} />
                                    <div className="flex items-center gap-0.5"><Star size={8} fill="#F5B544" style={{ color: '#F5B544' }} /><span className="text-[10px]" style={{ color: 'var(--muted)' }}>{r}</span></div>
                                    <span className="text-[9px] font-medium" style={{ color: 'var(--text)' }}>{count}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Modulos mais Ativados</h3>
                    {Object.keys(data.modulos_ativos || {}).length === 0 ? <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>Sem dados</p> : (
                        <div className="flex flex-col gap-2">
                            {Object.entries(data.modulos_ativos).sort((a, b) => b[1] - a[1]).map(([mod, count]) => (
                                <div key={mod} className="flex items-center gap-3">
                                    <span className="text-xs font-medium w-24 truncate" style={{ color: 'var(--text)' }}>{MODULE_LABELS[mod] || mod.replace(/_/g, ' ')}</span>
                                    <div className="flex-1 h-5 rounded-lg overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                                        <div className="h-full rounded-lg transition-all" style={{ width: `${(count / maxModCount) * 100}%`, background: 'rgba(var(--sv-accent-rgb), 0.2)', minWidth: 4 }} />
                                    </div>
                                    <span className="text-xs font-bold w-8 text-right" style={{ color: 'var(--sv-gold-primary)' }}>{count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Top Profissionais por Rating</h3>
                    {(data.top_profissionais || []).length === 0 ? <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>Sem avaliacoes ainda</p> : (
                        <div className="flex flex-col gap-2">
                            {data.top_profissionais.map((p, i) => (
                                <div key={p.profissional_id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: i === 0 ? 'rgba(var(--sv-accent-rgb), 0.06)' : 'var(--surface-2)' }}>
                                    <span className="text-xs font-bold w-5 text-center" style={{ color: i === 0 ? 'var(--sv-accent)' : 'var(--muted)' }}>#{i + 1}</span>
                                    <div className="flex-1 min-w-0"><span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{p.name}</span><span className="text-[10px] ml-1.5" style={{ color: 'var(--muted)' }}>{p.category}</span></div>
                                    <div className="flex items-center gap-1"><Star size={11} fill="#F5B544" style={{ color: '#F5B544' }} /><span className="text-xs font-bold" style={{ color: 'var(--sv-gold-primary)' }}>{p.avg_rating?.toFixed(1)}</span><span className="text-[10px]" style={{ color: 'var(--muted)' }}>({p.total_reviews})</span></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ==================== OFERTAS TAB ====================

const CHART_TOOLTIP_STYLE = { contentStyle: { background: '#0D2C63', border: '1px solid rgba(140,180,255,0.30)', borderRadius: 12, fontSize: 12, color: '#E5E5E5', padding: '10px 14px' }, cursor: { stroke: 'rgba(var(--sv-accent-rgb), 0.3)' } };
const PERIODS = [{ key: '7d', label: '7 dias' }, { key: '30d', label: '30 dias' }, { key: '90d', label: '90 dias' }];

function OfertasTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30d');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [showCustom, setShowCustom] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API}/admin/analytics/offers?period=${period}`;
            if (showCustom && customFrom && customTo) {
                url = `${API}/admin/analytics/offers?period=custom&from_date=${customFrom}T00:00:00&to_date=${customTo}T23:59:59`;
            }
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) setData(await res.json());
        } catch {} finally { setLoading(false); }
    }, [token, period, showCustom, customFrom, customTo]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const kpis = data?.kpis || {};
    const timeline = useMemo(() => (data?.timeline || []).map(t => ({ ...t, label: t.date?.substring(5) || '' })), [data]);
    const categories = data?.categories || [];
    const topOffers = data?.top_offers || [];
    const maxCatClicks = Math.max(1, ...categories.map(c => c.clicks));

    if (loading && !data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-4" data-testid="offers-analytics">
            {/* Period selector */}
            <div className="flex flex-wrap items-center gap-2">
                {PERIODS.map(p => (
                    <button key={p.key} data-testid={`period-${p.key}`} onClick={() => { setPeriod(p.key); setShowCustom(false); }}
                        className="px-3.5 py-2 rounded-lg text-xs font-medium transition-all"
                        style={{ background: !showCustom && period === p.key ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface)', border: `1px solid ${!showCustom && period === p.key ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: !showCustom && period === p.key ? 'var(--sv-accent)' : 'var(--muted)' }}>
                        {p.label}
                    </button>
                ))}
                <button data-testid="period-custom" onClick={() => setShowCustom(!showCustom)}
                    className="px-3.5 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5"
                    style={{ background: showCustom ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface)', border: `1px solid ${showCustom ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: showCustom ? 'var(--sv-accent)' : 'var(--muted)' }}>
                    <Calendar size={12} /> Custom
                </button>
                {loading && <Loader2 className="animate-spin ml-2" size={14} style={{ color: 'var(--muted)' }} />}
            </div>
            {showCustom && (
                <div className="flex flex-wrap items-center gap-2">
                    <input type="date" data-testid="custom-from" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>ate</span>
                    <input type="date" data-testid="custom-to" value={customTo} onChange={e => setCustomTo(e.target.value)} className="px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {[
                    { label: 'Compras (CTA)', value: kpis.total_clicks || 0, icon: MousePointerClick, color: '#22c55e', sub: 'cliques no botao de compra' },
                    { label: 'Views de Pagina', value: kpis.total_page_views || 0, icon: Eye, color: '#3B82F6', sub: 'visitas a pagina da oferta' },
                    { label: 'Cliques no Feed', value: kpis.total_detail_clicks || 0, icon: BarChart3, color: '#8B5CF6', sub: 'aberturas a partir do feed' },
                    { label: 'CTR', value: `${kpis.ctr || 0}%`, icon: TrendingUp, color: 'var(--sv-accent)', sub: 'compras / views de pagina' },
                    { label: 'Top Categoria', value: kpis.top_category || 'N/A', icon: Trophy, color: '#F59E0B', sub: 'mais compras no periodo', isText: true },
                ].map(k => (
                    <div key={k.label} className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, '-').replace(/[()]/g, '')}`}>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${k.color}12` }}>
                                <k.icon size={14} style={{ color: k.color }} />
                            </div>
                        </div>
                        <p className={`font-bold ${k.isText ? 'text-sm truncate' : 'text-xl'}`} style={{ color: k.isText ? 'var(--text)' : k.color, fontFamily: 'var(--font-display)' }}>{k.value}</p>
                        <p className="text-[10px] font-medium" style={{ color: 'var(--text)' }}>{k.label}</p>
                        <p className="text-[9px]" style={{ color: 'var(--muted)' }}>{k.sub}</p>
                    </div>
                ))}
            </div>

            {/* Timeline Chart */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Evolucao Temporal</h3>
                {timeline.length === 0 ? (
                    <p className="text-xs py-10 text-center" style={{ color: 'var(--muted)' }}>Sem dados no periodo selecionado</p>
                ) : (
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={timeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} width={30} />
                            <Tooltip {...CHART_TOOLTIP_STYLE} />
                            <Legend wrapperStyle={{ fontSize: 11, color: '#888' }} />
                            <Line type="monotone" dataKey="clicks" stroke="#22c55e" strokeWidth={2} dot={false} name="Cliques" />
                            <Line type="monotone" dataKey="views" stroke="#3B82F6" strokeWidth={2} dot={false} name="Views" />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Categories ranking */}
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Cliques por Categoria</h3>
                    {categories.length === 0 ? (
                        <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>Sem dados de categoria no periodo</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {categories.map((c, i) => (
                                <div key={c.category} className="flex items-center gap-3">
                                    <span className="text-xs font-bold w-5 text-center" style={{ color: i === 0 ? 'var(--sv-accent)' : 'var(--muted)' }}>#{i + 1}</span>
                                    <span className="text-xs font-medium w-28 truncate" style={{ color: 'var(--text)' }}>{c.category || 'Sem categoria'}</span>
                                    <div className="flex-1 h-5 rounded-lg overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                                        <div className="h-full rounded-lg transition-all" style={{ width: `${(c.clicks / maxCatClicks) * 100}%`, background: 'rgba(34,197,94,0.2)', minWidth: 4 }} />
                                    </div>
                                    <span className="text-xs font-bold w-8 text-right" style={{ color: '#22c55e' }}>{c.clicks}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Top Offers ranking */}
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Top Ofertas</h3>
                    {topOffers.length === 0 ? (
                        <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>Sem cliques no periodo</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {topOffers.map((o, i) => (
                                <div key={o.deal_id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: i === 0 ? 'rgba(var(--sv-accent-rgb), 0.06)' : 'var(--surface-2)' }}>
                                    <span className="text-xs font-bold w-5 text-center flex-shrink-0" style={{ color: i === 0 ? 'var(--sv-accent)' : 'var(--muted)' }}>#{i + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{o.title}</p>
                                        <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{o.store}</p>
                                    </div>
                                    <div className="flex flex-col items-end flex-shrink-0">
                                        <span className="text-xs font-bold" style={{ color: '#22c55e' }}>{o.clicks} cliques</span>
                                        {o.ctr > 0 && <span className="text-[9px]" style={{ color: 'var(--muted)' }}>CTR {o.ctr}%</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Data transparency */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                <p className="text-[10px] leading-relaxed" style={{ color: 'var(--muted)' }}>
                    <strong style={{ color: 'var(--text)' }}>Sobre os dados:</strong> CTR = compras (deal_buy_click) / views de pagina (deal_view). Cliques no feed (deal_detail_click) sao contabilizados separadamente. Dados historicos anteriores a esta versao podem nao ter category_id ou deal_view registrados. Novos eventos ja entram com tracking completo.
                </p>
            </div>
        </div>
    );
}

// ==================== CANAIS TAB ====================

const SOURCE_META = {
    web: { label: 'Web (Site)', color: '#3B82F6' },
    telegram: { label: 'Telegram', color: '#0EA5E9' },
    mercadolivre: { label: 'Mercado Livre', color: '#FFE600' },
    promobit: { label: 'Promobit', color: '#FF6B35' },
    amazon: { label: 'Amazon', color: '#FF9900' },
    manual: { label: 'Manual', color: '#8B5CF6' },
    crawler: { label: 'Crawler', color: '#10B981' },
    unknown: { label: 'Outros', color: '#6B7280' },
};

function CanaisTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30d');
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/track/channels?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) setData(await res.json());
        } catch {} finally { setLoading(false); }
    }, [token, period]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading && !data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const bySource = data?.by_source || [];
    const detail = data?.source_detail || {};
    const timeline = data?.timeline || [];
    const dealsBySource = data?.deals_by_source || [];
    const discoveriesBySource = data?.discoveries_by_source || [];
    const maxEvents = Math.max(1, ...bySource.map(s => s.count));
    const maxDeals = Math.max(1, ...dealsBySource.map(s => s.count));
    const maxDisc = Math.max(1, ...discoveriesBySource.map(s => s.count));
    const allSources = [...new Set([...bySource.map(s => s.source), ...Object.keys(detail)])];

    return (
        <div className="space-y-4" data-testid="channels-analytics">
            <div className="flex flex-wrap items-center gap-2">
                {PERIODS.map(p => (
                    <button key={p.key} data-testid={`channel-period-${p.key}`} onClick={() => setPeriod(p.key)}
                        className="px-3.5 py-2 rounded-lg text-xs font-medium transition-all"
                        style={{ background: period === p.key ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface)', border: `1px solid ${period === p.key ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: period === p.key ? 'var(--sv-accent)' : 'var(--muted)' }}>
                        {p.label}
                    </button>
                ))}
                {loading && <Loader2 className="animate-spin ml-2" size={14} style={{ color: 'var(--muted)' }} />}
            </div>

            {/* Source Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {bySource.slice(0, 4).map(s => {
                    const meta = SOURCE_META[s.source] || SOURCE_META.unknown;
                    const bd = detail[s.source]?.breakdown || {};
                    const clicks = bd.deal_buy_click || 0;
                    const views = bd.deal_view || 0;
                    return (
                        <div key={s.source} className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`source-card-${s.source}`}>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 rounded-full" style={{ background: meta.color }} />
                                <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{meta.label}</span>
                            </div>
                            <p className="text-xl font-bold" style={{ color: meta.color, fontFamily: 'var(--font-display)' }}>{s.count}</p>
                            <p className="text-[10px]" style={{ color: 'var(--muted)' }}>eventos no periodo</p>
                            <div className="flex gap-3 mt-2">
                                <span className="text-[10px]" style={{ color: '#22c55e' }}>{clicks} compras</span>
                                <span className="text-[10px]" style={{ color: '#3B82F6' }}>{views} views</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Timeline by Source */}
            {timeline.length > 0 && (
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Eventos por Canal (timeline)</h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={timeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} tickFormatter={v => v?.substring(5) || ''} />
                            <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} width={30} />
                            <Tooltip {...CHART_TOOLTIP_STYLE} />
                            <Legend wrapperStyle={{ fontSize: 11, color: '#888' }} />
                            {allSources.map(src => {
                                const meta = SOURCE_META[src] || SOURCE_META.unknown;
                                return <Bar key={src} dataKey={src} stackId="a" fill={meta.color} name={meta.label} />;
                            })}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Events by source bar */}
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Eventos por Fonte</h3>
                    {bySource.length === 0 ? (
                        <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>Sem dados</p>
                    ) : (
                        <div className="flex flex-col gap-2.5">
                            {bySource.map(s => {
                                const meta = SOURCE_META[s.source] || SOURCE_META.unknown;
                                return (
                                    <div key={s.source} className="flex items-center gap-3">
                                        <span className="text-xs font-medium w-24 truncate flex items-center gap-1.5" style={{ color: meta.color }}>
                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                                            {meta.label}
                                        </span>
                                        <div className="flex-1 h-5 rounded-lg overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                                            <div className="h-full rounded-lg transition-all" style={{ width: `${(s.count / maxEvents) * 100}%`, background: `${meta.color}30`, minWidth: 4 }} />
                                        </div>
                                        <span className="text-xs font-bold w-10 text-right" style={{ color: meta.color }}>{s.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Deals by source */}
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Ofertas por Fonte</h3>
                    {dealsBySource.length === 0 ? (
                        <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>Sem dados</p>
                    ) : (
                        <div className="flex flex-col gap-2.5">
                            {dealsBySource.map(s => {
                                const meta = SOURCE_META[s.source] || SOURCE_META.unknown;
                                return (
                                    <div key={s.source} className="flex items-center gap-3">
                                        <span className="text-xs font-medium w-24 truncate flex items-center gap-1.5" style={{ color: meta.color }}>
                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                                            {meta.label}
                                        </span>
                                        <div className="flex-1 h-5 rounded-lg overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                                            <div className="h-full rounded-lg transition-all" style={{ width: `${(s.count / maxDeals) * 100}%`, background: `${meta.color}30`, minWidth: 4 }} />
                                        </div>
                                        <span className="text-xs font-bold w-10 text-right" style={{ color: meta.color }}>{s.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Discoveries by source */}
                <div className="rounded-xl p-4 sm:p-5 lg:col-span-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Descobertas por Fonte (Crawler)</h3>
                    {discoveriesBySource.length === 0 ? (
                        <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>Sem dados de crawler</p>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            {discoveriesBySource.map(s => {
                                const meta = SOURCE_META[s.source] || SOURCE_META.unknown;
                                return (
                                    <div key={s.source} className="rounded-lg p-3" style={{ background: `${meta.color}08`, border: `1px solid ${meta.color}15` }}>
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                                            <span className="text-xs font-medium" style={{ color: meta.color }}>{meta.label}</span>
                                        </div>
                                        <p className="text-lg font-bold" style={{ color: meta.color, fontFamily: 'var(--font-display)' }}>{s.count}</p>
                                        <p className="text-[10px]" style={{ color: 'var(--muted)' }}>descobertas</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="rounded-lg p-3" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                <p className="text-[10px] leading-relaxed" style={{ color: 'var(--muted)' }}>
                    <strong style={{ color: 'var(--text)' }}>Sobre os canais:</strong> Fontes incluem Web (site direto), Telegram, Mercado Livre, Promobit, Amazon e importacoes manuais. O crawler coleta ofertas de multiplas fontes automaticamente. Eventos rastreiam interacoes dos usuarios com as ofertas por origem.
                </p>
            </div>
        </div>
    );
}

// ==================== MAIN PAGE ====================

const TABS = [
    { key: 'plataforma', label: 'Plataforma', icon: Activity },
    { key: 'ofertas', label: 'Ofertas', icon: MousePointerClick },
    { key: 'canais', label: 'Canais', icon: Globe },
];

export default function AdminAnalyticsPage() {
    const [tab, setTab] = useState('plataforma');
    const [data, setData] = useState(null);
    const [events, setEvents] = useState(null);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    const fetchData = useCallback(async () => {
        try {
            const [res, evRes] = await Promise.all([
                fetch(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(`${API}/track/stats`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            if (res.ok) setData(await res.json());
            if (evRes.ok) setEvents(await evRes.json());
        } catch {} finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Read tab from URL query
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const t = params.get('tab');
        if (t && TABS.some(tb => tb.key === t)) setTab(t);
    }, []);

    if (loading && !data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div data-testid="admin-analytics-page">
            {/* Internal tabs */}
            <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="analytics-tabs">
                {TABS.map(t => (
                    <button key={t.key} data-testid={`analytics-tab-${t.key}`} onClick={() => setTab(t.key)}
                        className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-medium transition-all flex-1 justify-center"
                        style={{
                            background: tab === t.key ? 'rgba(var(--sv-accent-rgb), 0.12)' : 'transparent',
                            color: tab === t.key ? 'var(--sv-accent)' : 'var(--muted)',
                            fontWeight: tab === t.key ? 600 : 500,
                        }}>
                        <t.icon size={13} />
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'plataforma' && <PlataformaTab data={data} events={events} />}
            {tab === 'ofertas' && <OfertasTab />}
            {tab === 'canais' && <CanaisTab />}
        </div>
    );
}
